import { useEffect, useRef, useState } from 'react';
import { isAwaitingBrowser } from '@cotera/client/v0/actions/framework';
import { useTenantedClient } from '@cotera/client/app/stores/org';
import type { ToolRendererProps } from '@cotera/client/v0/chat/tool-renderers';

/**
 * Answers a tool call that has suspended waiting on this browser.
 *
 * Shared by every renderer that completes an interrupt, because the parts that
 * are easy to get wrong are the same each time: acting only once the run has
 * actually stopped on this call rather than while its arguments are still
 * streaming, answering exactly once per call however often the transcript
 * re-renders, and surfacing a failed submission instead of leaving the chat
 * looking hung.
 */
export function useAnswerInterruptedTool(params: {
  part: ToolRendererProps['part'];
  chatModel: ToolRendererProps['chatModel'];
  readonly?: boolean;
  /**
   * Produces the result to send back. Called at most once per tool call; throwing
   * is answered with the error rather than leaving the call open.
   */
  answer: () => Promise<Record<string, unknown>>;
}): { awaiting: boolean; failure: string | null } {
  const { part, chatModel, readonly, answer } = params;
  const client = useTenantedClient();
  const [failure, setFailure] = useState<string | null>(null);

  const toolCallId = part.toolCallId;
  const awaiting = isAwaitingBrowser(part.getOutput()) && readonly !== true;

  /** Answered once per call: the transcript re-renders, the run resumes once. */
  const answered = useRef<string | null>(null);
  // Read through a ref so a re-render cannot restart work already in flight.
  const latest = useRef({ client, answer });
  latest.current = { client, answer };

  useEffect(() => {
    if (!awaiting || answered.current === toolCallId) {
      return;
    }
    answered.current = toolCallId;

    void (async () => {
      const { client: api, answer: produce } = latest.current;
      const send = async (input: Record<string, unknown>): Promise<void> => {
        const result = await api.resource.chat.runInterruptedTool({
          conversationId: chatModel.id,
          toolCallId,
          model: chatModel.model,
          input,
        });
        if (result.isErr()) {
          // Loud: the symptom otherwise is a tool that never returns and a chat
          // that looks hung, with nothing anywhere to point at.
          // eslint-disable-next-line no-console
          console.error(
            `[interrupted-tool] could not answer ${toolCallId}`,
            result.error
          );
          setFailure('Could not send the result back to Coco.');
          // Released so a later render can retry rather than leaving the run
          // waiting on a browser that has stopped trying.
          answered.current = null;
        }
      };

      try {
        await send(await produce());
      } catch (e) {
        const message =
          e instanceof Error ? e.message : 'Something went wrong.';
        await send({ error: message });
      }
    })();
    // `answer` deliberately absent: it closes over render-scoped values and
    // would restart an answer that is already on its way.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaiting, toolCallId, chatModel.id, chatModel.model]);

  return { awaiting, failure };
}
